import { createContext, useContext, useMemo, useState } from 'react';
import * as btc from '@scure/btc-signer';

import { getNetworkConfig, Network } from ':fat/core/networks';
import { useFiatBalances } from ':fat/hooks/useBalances';
import { EthTransaction, Transaction } from ':fat/workers/blockchain/types';

const TransactionContext = createContext<{
  transactions: Record<Network, Transaction[]>;
  setTransactions: (txs: Record<Network, Transaction[]>) => void;
}>({ transactions: {} as Record<Network, Transaction[]>, setTransactions: () => {} });

type TransactionProviderProps = {
  children: React.ReactNode;
};

export function TransactionProvider({ children }: TransactionProviderProps) {
  const [transactions, setTransactions] = useState<Record<Network, Transaction[]>>(
    {} as Record<Network, Transaction[]>,
  );

  const value = useMemo(() => ({ transactions, setTransactions }), [transactions, setTransactions]);

  return <TransactionContext.Provider value={value}>{children}</TransactionContext.Provider>;
}

export function useTransactions() {
  const { transactions, setTransactions } = useContext(TransactionContext);

  const networksWithTransactions = Object.keys(transactions).filter(
    (network) => !!transactions[network as Network]?.length,
  );

  const anyTransactions =
    !!networksWithTransactions.length || !!Object.values(transactions).flat().length;

  const destinationAddresses = Object.fromEntries(
    networksWithTransactions.map((n) => {
      const network = n as Network;
      const txs = transactions[network];
      if (!txs?.length) {
        return [network, ''];
      }

      const tx = txs[0];
      if (tx instanceof btc.Transaction && network !== 'eth') {
        const networkConfig = getNetworkConfig(network);
        const destinationOutput = tx.getOutput(tx.outputsLength - 1);
        const script = destinationOutput?.script ?? new Uint8Array();
        const address = btc.Address(networkConfig).encode(btc.OutScript.decode(script));
        return [network, address];
      }

      const ethTx = tx as EthTransaction;
      return [network, ethTx.tx.to as string];
    }),
  );

  const cryptoInputAmounts = Object.fromEntries(
    networksWithTransactions.map((network) => [
      network,
      transactions[network as Network]?.reduce((total, tx) => total + calculateInputValue(tx), 0n),
    ]),
  );

  const cryptoNetworkFeeAmounts = Object.fromEntries(
    networksWithTransactions.map((network) => [
      network,
      transactions[network as Network]?.reduce(
        (total, tx) =>
          total +
          (tx instanceof btc.Transaction ? tx.fee : (tx.tx.maxFeePerGas ?? 0n) * (tx.tx.gas ?? 0n)),
        0n,
      ),
    ]),
  );

  const cryptoCoinbaseFeeAmounts = Object.fromEntries(
    networksWithTransactions.map((network) => [
      network,
      transactions[network as Network]?.reduce(
        (total, tx) => total + calculateCoinbaseFeeValue(tx),
        0n,
      ),
    ]),
  );

  const fiatInputAmounts = useFiatBalances(cryptoInputAmounts);
  const fiatNetworkFeeAmounts = useFiatBalances(cryptoNetworkFeeAmounts);
  const fiatCoinbaseFeeAmounts = useFiatBalances(cryptoCoinbaseFeeAmounts);

  const totalInput = Object.values(fiatInputAmounts).reduce((a, b) => a + b, 0);
  const totalNetworkFee = Object.values(fiatNetworkFeeAmounts).reduce((a, b) => a + b, 0);
  const totalCoinbaseFee = Object.values(fiatCoinbaseFeeAmounts).reduce((a, b) => a + b, 0);
  const totalProceeds = totalInput - totalNetworkFee - totalCoinbaseFee;

  return {
    transactions,
    setTransactions,
    networksWithTransactions,
    anyTransactions,
    destinationAddresses,
    cryptoInputAmounts,
    cryptoNetworkFeeAmounts,
    cryptoCoinbaseFeeAmounts,
    fiatInputAmounts,
    fiatNetworkFeeAmounts,
    fiatCoinbaseFeeAmounts,
    totalInput,
    totalNetworkFee,
    totalCoinbaseFee,
    totalProceeds,
  };
}

export function calculateInputValue(tx: Transaction) {
  let txInput = 0n;

  if (tx instanceof btc.Transaction) {
    for (let i = 0; i < tx.inputsLength; i++) {
      txInput += tx.getInput(i)?.witnessUtxo?.amount ?? 0n;
    }
  } else {
    return (tx.tx.value ?? 0n) + (tx.tx.maxFeePerGas ?? 0n) * (tx.tx.gas ?? 0n);
  }

  return txInput;
}

export function calculateCoinbaseFeeValue(tx: Transaction) {
  // No Ethereum txs have a Coinbase fee, since those were either not charged at
  // the time (for xpk addresses) or were taken in the flushing contracts
  // (for contract addresses)
  return tx instanceof btc.Transaction
    ? tx.outputsLength > 1 // If there is more than one output, the first one is the fee
      ? tx.getOutput(0)?.amount ?? 0n
      : 0n
    : 0n;
}

export function calculateDestinationValue(tx: Transaction) {
  return tx instanceof btc.Transaction
    ? tx.getOutput(0)?.amount ?? 0n // The last output is always the destination one
    : tx.tx.value ?? 0n;
}
